import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { bankName } from "../../util/config";
import {
  HiShieldCheck,
  HiSparkles,
  HiUsers,
  HiArrowPath,
  HiUserPlus,
  HiDocumentPlus,
  HiLockClosed,
  HiArrowRightOnRectangle,
  HiSquares2X2,
  HiInformationCircle,
  HiChevronRight,
  HiKey,
} from "react-icons/hi2";

import adminUi from "../UI/AdminUi.module.css";
import classes from "./HomePage.module.css";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.06 * i, duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CARDS = [
  {
    title: "Accounts",
    text: "Review balances, update profiles, and manage access.",
    to: "/accounts",
    Icon: HiUsers,
  },
  {
    title: "Transactions",
    text: "Audit transfers, reverse entries, and resolve exceptions.",
    to: "/transactions",
    Icon: HiArrowPath,
  },
  {
    title: "Create account",
    text: "Onboard a new customer with credentials and opening balance.",
    to: "/createAccount",
    Icon: HiUserPlus,
  },
  {
    title: "Create transaction",
    text: "Record domestic, international, or in-person movements.",
    to: "/createTransaction",
    Icon: HiDocumentPlus,
  },
];

const HomePage = () => {
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
  const userName = useSelector((s) => s.auth.user?.name);

  return (
    <div className={classes.page}>
      <section className={classes.hero}>
        <div className={classes.heroOverlay} aria-hidden />
        <motion.div
          className={classes.heroInner}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className={classes.eyebrow}>
            <HiShieldCheck className={classes.eyebrowIcon} aria-hidden />
            {bankName}
          </p>
          <div className={classes.heroTitleRow}>
            <HiSparkles className={classes.heroTitleIcon} aria-hidden />
            <h1 className={classes.heroTitle}>Administration portal</h1>
          </div>
          <p className={classes.heroLead}>
            {isLoggedIn
              ? `Welcome back${userName ? `, ${userName}` : ""}. Choose a workspace below or use the menu.`
              : "Secure hub for customer accounts, transfers, and compliance-ready records."}
          </p>
          <div className={classes.heroActions}>
            {isLoggedIn ? (
              <>
                <Link className={classes.btnPrimary} to="/accounts">
                  <HiUsers className={classes.btnIcon} aria-hidden />
                  View accounts
                </Link>
                <Link className={classes.btnGhost} to="/transactions">
                  <HiArrowPath className={classes.btnIcon} aria-hidden />
                  Transactions
                </Link>
              </>
            ) : (
              <>
                <Link className={classes.btnPrimary} to="/login">
                  <HiKey className={classes.btnIcon} aria-hidden />
                  Administrator sign in
                </Link>
                <span className={classes.heroHint}>
                  <HiLockClosed className={classes.heroHintIcon} aria-hidden />
                  Authorised personnel only
                </span>
              </>
            )}
          </div>
        </motion.div>
      </section>

      <section className={classes.panel}>
        <motion.h2
          className={classes.sectionTitle}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={fadeUp}
          custom={0}
        >
          <HiSquares2X2 className={classes.sectionTitleIcon} aria-hidden />
          {isLoggedIn ? "Quick access" : "What you can do here"}
        </motion.h2>
        <div className={classes.cards}>
          {CARDS.map((card, i) => {
            const Icon = card.Icon;
            return (
              <motion.div
                key={card.title}
                className={classes.card}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-30px" }}
                variants={fadeUp}
                custom={i + 1}
              >
                <div className={classes.cardTitleRow}>
                  <Icon className={classes.cardIcon} aria-hidden />
                  <h3>{card.title}</h3>
                </div>
                <p>{card.text}</p>
                {isLoggedIn ? (
                  <Link to={card.to} className={classes.cardLink}>
                    Open
                    <HiChevronRight
                      className={classes.cardLinkIcon}
                      aria-hidden
                    />
                  </Link>
                ) : (
                  <Link to="/login" className={classes.cardLinkMuted}>
                    <HiArrowRightOnRectangle
                      className={classes.cardLinkIcon}
                      aria-hidden
                    />
                    Sign in to access
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className={`${adminUi.page} ${classes.bottom}`}>
        <motion.p
          className={classes.trust}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <HiInformationCircle className={classes.trustIcon} aria-hidden />
          <span>
            Operations are subject to FINMA oversight. Keep credentials
            confidential and sign out on shared devices.
          </span>
        </motion.p>
      </section>
    </div>
  );
};

export default HomePage;
